import { useEffect, useRef } from 'react'
import { getWebSocketConnectionStats, getWebSocketState } from '@/shared/api/api.websocket'
import { useConnectionStatusStore } from '@/shared/lib/connection-status'
import { useRealtimeStore } from '../model/realtime.store'

/**
 * Hook that monitors WebSocket connection and updates the realtime store
 * This replaces the useWebSocketConnectionMonitor functionality but integrates with the store
 */
export function useRealtimeConnectionMonitor() {
  const { shouldConnect, updateConnectionStatus } = useRealtimeStore()
  const { setRealtimeConnected } = useConnectionStatusStore()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const statsLogCounterRef = useRef(0)

  useEffect(() => {
    if (!shouldConnect) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      updateConnectionStatus(false, '')
      setRealtimeConnected(false)
      return
    }

    const checkConnection = () => {
      const wsState = getWebSocketState()

      updateConnectionStatus(wsState.isConnected, wsState.lastReceivedTime)

      setRealtimeConnected(wsState.isConnected)

      // Log statistics periodically (every 30 seconds) to avoid console spam
      statsLogCounterRef.current += 1
      if (statsLogCounterRef.current >= 30) {
        const stats = getWebSocketConnectionStats()
        if (import.meta.env.VITE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.log('[WebSocket Monitor] Connection Stats:', stats)
        }
        statsLogCounterRef.current = 0
      }
    }

    checkConnection()

    // Setup polling
    intervalRef.current = setInterval(checkConnection, 1000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [shouldConnect, updateConnectionStatus, setRealtimeConnected])
}
